'use client';

import { AbsoluteCenter, Menu as ChakraMenu } from '@chakra-ui/react';
import { default as CheckIcon } from '~icons/lucide/check';
import { default as ChevronRightIcon } from '~icons/lucide/chevron-right';

import { Portal } from './Portal';

type MenuContentProps = ChakraMenu.ContentProps & {
  portalled?: boolean;
  portalRef?: React.RefObject<HTMLElement>;
  ref?: React.RefObject<HTMLDivElement>;
  /** Positioner overrides — e.g. a zIndex above a fullscreen overlay the trigger lives in. */
  positionerProps?: ChakraMenu.PositionerProps;
};

export const MenuContent = ({
  portalled = true,
  portalRef,
  ref,
  positionerProps,
  ...rest
}: MenuContentProps) => {
  return (
    <Portal portalled={portalled} portalRef={portalRef}>
      <ChakraMenu.Positioner {...positionerProps}>
        <ChakraMenu.Content ref={ref} {...rest} />
      </ChakraMenu.Positioner>
    </Portal>
  );
};

type MenuArrowProps = ChakraMenu.ArrowProps & {
  ref?: React.RefObject<HTMLDivElement>;
};

export const MenuArrow = ({ ref, ...props }: MenuArrowProps) => {
  return (
    <ChakraMenu.Arrow ref={ref} {...props}>
      <ChakraMenu.ArrowTip />
    </ChakraMenu.Arrow>
  );
};

type MenuCheckboxItemProps = ChakraMenu.CheckboxItemProps & {
  ref?: React.RefObject<HTMLDivElement>;
};

export const MenuCheckboxItem = ({ ref, ...props }: MenuCheckboxItemProps) => {
  return (
    <ChakraMenu.CheckboxItem ps="8" ref={ref} {...props}>
      <AbsoluteCenter axis="horizontal" insetStart="4" asChild>
        <ChakraMenu.ItemIndicator>
          <CheckIcon />
        </ChakraMenu.ItemIndicator>
      </AbsoluteCenter>
      {props.children}
    </ChakraMenu.CheckboxItem>
  );
};

type MenuRadioItemProps = ChakraMenu.RadioItemProps & {
  ref?: React.RefObject<HTMLDivElement>;
};

export const MenuRadioItem = ({ ref, ...props }: MenuRadioItemProps) => {
  const { children, ...rest } = props;
  return (
    <ChakraMenu.RadioItem ps="8" ref={ref} {...rest}>
      <AbsoluteCenter axis="horizontal" insetStart="4" asChild>
        <ChakraMenu.ItemIndicator>
          <CheckIcon />
        </ChakraMenu.ItemIndicator>
      </AbsoluteCenter>
      <ChakraMenu.ItemText>{children}</ChakraMenu.ItemText>
    </ChakraMenu.RadioItem>
  );
};

type MenuItemGroupProps = ChakraMenu.ItemGroupProps & {
  ref?: React.RefObject<HTMLDivElement>;
};

export const MenuItemGroup = ({ ref, ...props }: MenuItemGroupProps) => {
  const { title, children, ...rest } = props;
  return (
    <ChakraMenu.ItemGroup ref={ref} {...rest}>
      {title && <ChakraMenu.ItemGroupLabel userSelect="none">{title}</ChakraMenu.ItemGroupLabel>}
      {children}
    </ChakraMenu.ItemGroup>
  );
};

type MenuTriggerItemProps = ChakraMenu.TriggerItemProps & {
  startIcon?: React.ReactNode;
  ref?: React.RefObject<HTMLDivElement>;
};

export const MenuTriggerItem = ({ ref, ...props }: MenuTriggerItemProps) => {
  const { startIcon, children, ...rest } = props;
  return (
    <ChakraMenu.TriggerItem ref={ref} {...rest}>
      {startIcon}
      {children}
      <ChevronRightIcon />
    </ChakraMenu.TriggerItem>
  );
};

export const MenuRadioItemGroup = ChakraMenu.RadioItemGroup;
export const MenuContextTrigger = ChakraMenu.ContextTrigger;
export const MenuRoot = ChakraMenu.Root;
export const MenuSeparator = ChakraMenu.Separator;

export const MenuItem = ChakraMenu.Item;
export const MenuItemText = ChakraMenu.ItemText;
export const MenuItemCommand = ChakraMenu.ItemCommand;
export const MenuTrigger = ChakraMenu.Trigger;
